/**
 * F-90 import wording — pure strings, no React. The confirm dialog and the
 * completion toast are where an import either reassures the user or silently
 * lies to them, so the sentences are testable on their own.
 */
import type { ImportScriptResult } from "./import-script.js";
import type { ImportPreview } from "./script-file.js";

const count = (n: number): string => n.toLocaleString("en-US");

/** "10,000 lines" / "1 line" — the dialog's headline number. */
export function lineCount(n: number): string {
  return `${count(n)} ${n === 1 ? "line" : "lines"}`;
}

/**
 * Everything the confirm dialog must say OUT LOUD before the user commits —
 * each item is something the import silently does otherwise:
 *  - a multi-column file loses every column but the first (F-90 imports ONE
 *    column: the Original Script column);
 *  - blank lines are dropped (`parseScript`).
 * Empty when the file is a plain one-column script — then the dialog is just
 * "N lines → Original Script", with nothing to warn about.
 */
export function importCaveats(preview: ImportPreview): string[] {
  const caveats: string[] = [];
  if (preview.fields > 1) {
    const dropped = preview.fields - 1;
    caveats.push(
      `The file has ${preview.fields} columns — only the first is imported; ${
        dropped === 1 ? "the other column is" : `the other ${dropped} columns are`
      } ignored.`,
    );
  }
  if (preview.blankSkipped > 0) {
    caveats.push(
      `${count(preview.blankSkipped)} blank ${
        preview.blankSkipped === 1 ? "line was" : "lines were"
      } skipped.`,
    );
  }
  return caveats;
}

/** The dialog's message for a file with nothing to import. */
export function nothingToImportMessage(filename: string): string {
  return `${filename} has no lines to import.`;
}

/**
 * The completion toast. It reports what was WRITTEN, not what was asked for:
 * an import that stops early (F-45 froze the board mid-run) must not claim the
 * rows it never wrote — the user needs to know to run it again.
 */
export function importDoneMessage(
  result: ImportScriptResult,
  requested: number,
  columnName: string,
): string {
  if (result.written === 0) {
    return `Nothing was imported into ${columnName} — the board is read-only right now.`;
  }
  if (result.written < requested) {
    return `Imported ${lineCount(result.written)} of ${count(
      requested,
    )} into ${columnName} — the board went read-only before the rest could be written. Import the file again to finish.`;
  }
  return `Imported ${lineCount(result.written)} into ${columnName}.`;
}
